import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import * as XLSX from 'xlsx';
import CadastroDataTable from '../../components/CadastroDataTable/CadastroDataTable';
import { richItemTemplate, richValueTemplate } from '../../components/RichSelectOption';
import '../../components/RichSelectOption.css';
import {
  buildAgenteFilterOptions,
  buildFilialFilterOptions,
  mesAtualRange,
  downloadFromApi,
  openPdfFromContent,
} from '../../utils/filtroHelpers';
import {
  toIsoDateLocal,
  loadFiltrosPersistidos,
  saveFiltrosPersistidos,
  filtrosTemDatas,
  buildExportFilterParams,
} from '../../utils/filtroPersistencia';
import api, { API_BASE_URL, normalize } from '../../services/api';
import { downloadModeloNfseExcel } from '../../services/nfseExcel';
import './NfseConsulta.css';

const FILTROS_STORAGE_KEY = 'nfse-filtros:emissor';

const STATUS_EMISSOR = [
  { label: 'Todos', value: '' },
  { label: 'Rascunho', value: 'Rascunho' },
  { label: 'Validando', value: 'Validando' },
  { label: 'Processando', value: 'Processando' },
  { label: 'Autorizada', value: 'Autorizada' },
  { label: 'Rejeitada', value: 'Rejeitada' },
  { label: 'Cancelada', value: 'Cancelada' },
  { label: 'Substituída', value: 'Substituida' },
  { label: 'Erro comunicação', value: 'ErroComunicacao' },
];

const FILTROS_OPCIONAIS = [
  { id: 'numero', label: 'Número NFS-e' },
  { id: 'chave', label: 'Chave de acesso' },
  { id: 'serie', label: 'Série DPS' },
];

const EMPTY_FILTROS = () => {
  const { dataInicio, dataFim } = mesAtualRange();
  return {
    dataInicio,
    dataFim,
    filialId: null,
    agenteId: null,
    status: '',
    numero: '',
    chave: '',
    serie: '',
  };
};

function statusSeverity(status) {
  switch (status) {
    case 'Autorizada':
      return 'success';
    case 'Rascunho':
      return 'info';
    case 'Processando':
    case 'Validando':
      return 'warning';
    case 'Rejeitada':
    case 'ErroComunicacao':
    case 'Cancelada':
      return 'danger';
    default:
      return 'secondary';
  }
}

function loadFiltrosVisiveis() {
  try {
    const raw = localStorage.getItem('nfse-filtros-visiveis:emissor');
    if (!raw) return FILTROS_OPCIONAIS.map((f) => f.id);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : FILTROS_OPCIONAIS.map((f) => f.id);
  } catch {
    return FILTROS_OPCIONAIS.map((f) => f.id);
  }
}

function errMsg(e, fallback) {
  return e?.response?.data?.message || fallback;
}

export default function NfseEmissorPage() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const fileRef = useRef(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [filtros, setFiltros] = useState(() =>
    loadFiltrosPersistidos(FILTROS_STORAGE_KEY, EMPTY_FILTROS)
  );
  const [aplicados, setAplicados] = useState(() =>
    loadFiltrosPersistidos(FILTROS_STORAGE_KEY, EMPTY_FILTROS)
  );
  const [empresa, setEmpresa] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState([]);
  const [filtrosCfgVisible, setFiltrosCfgVisible] = useState(false);
  const [filtrosVisiveis, setFiltrosVisiveis] = useState(loadFiltrosVisiveis);
  const [draftVisiveis, setDraftVisiveis] = useState(loadFiltrosVisiveis);
  const [importVisible, setImportVisible] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importLinhas, setImportLinhas] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importando, setImportando] = useState(false);
  const [emailVisible, setEmailVisible] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailDest, setEmailDest] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [detalheVisible, setDetalheVisible] = useState(false);
  const [emailLote, setEmailLote] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [eRes, fRes, aRes] = await Promise.all([
          api.get('/empresas/me').catch(() => ({ data: null })),
          api.get('/filiais').catch(() => ({ data: [] })),
          api.get('/agentes').catch(() => ({ data: [] })),
        ]);
        setEmpresa(normalize(eRes.data));
        setFiliais(normalize(fRes.data));
        setAgentes(normalize(aRes.data));
      } catch {
        // lookups opcionais
      }
    };
    loadLookups();
  }, []);

  const carregar = useCallback(async (f = aplicados) => {
    setLoading(true);
    try {
      const params = {};
      if (f.dataInicio) params.dataInicio = toIsoDateLocal(f.dataInicio);
      if (f.dataFim) params.dataFim = toIsoDateLocal(f.dataFim);
      if (f.filialId != null) params.filialId = f.filialId;
      if (f.agenteId != null) params.agenteId = f.agenteId;
      if (f.status) params.status = f.status;
      if (f.numero) params.numero = f.numero;
      if (f.chave) params.chave = f.chave;
      if (f.serie) params.serie = f.serie;
      const { data } = await api.get('/nfse/emitidas', { params });
      setRows(normalize(data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: errMsg(e, 'Falha ao carregar NFS-e emitidas.'),
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [aplicados]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filialOptions = useMemo(
    () => buildFilialFilterOptions(empresa, filiais, { incluirTodas: true }),
    [empresa, filiais]
  );

  const agenteOptions = useMemo(
    () =>
      buildAgenteFilterOptions(agentes, {
        filialId: filtros.filialId,
        incluirTodos: true,
        todosLabel: 'Todos',
      }),
    [agentes, filtros.filialId]
  );

  const setCampo = (campo, valor) =>
    setFiltros((p) => {
      const next = { ...p, [campo]: valor };
      if (campo === 'filialId') next.agenteId = null;
      return next;
    });

  const aplicar = () => {
    const next = { ...filtros };
    setAplicados(next);
    setSelection([]);
    saveFiltrosPersistidos(FILTROS_STORAGE_KEY, next);
  };

  const limpar = () => {
    const empty = EMPTY_FILTROS();
    setFiltros(empty);
    setAplicados(empty);
    setSelection([]);
    saveFiltrosPersistidos(FILTROS_STORAGE_KEY, empty);
  };

  const money = (v) =>
    Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  /** Só rascunhos / falhas podem ir para emissão — nunca Autorizada/Cancelada/etc. */
  const podeEmitirStatus = (status) => {
    const s = String(status || '');
    return s === 'Rascunho' || s === 'Rejeitada' || s === 'ErroComunicacao';
  };

  const podeEditarStatus = (status) => podeEmitirStatus(status);

  const editarNota = (r) => {
    if (!podeEditarStatus(r?.status)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Edição',
        detail: 'Só Rascunho, Rejeitada ou Erro de comunicação podem ser editados.',
      });
      return;
    }
    navigate(`/nfse/emissor/nova?id=${r.id}`);
  };

  const emitirSelecao = () => {
    const selecionadas = selection || [];
    if (!selecionadas.length) return;

    const emitiveis = selecionadas.filter((r) => podeEmitirStatus(r.status));
    const bloqueadas = selecionadas.filter((r) => !podeEmitirStatus(r.status));

    if (!emitiveis.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Emissão',
        detail:
          'Nenhuma nota selecionada pode ser emitida. Só Rascunho / Rejeitada / Erro de comunicação (não Autorizada).',
        life: 8000,
      });
      return;
    }

    const nums = emitiveis
      .slice(0, 8)
      .map((r) => r.numeroNfse || `#${r.id}`)
      .join(', ');
    const extra = emitiveis.length > 8 ? ` (+${emitiveis.length - 8})` : '';
    const avisoBloq =
      bloqueadas.length > 0
        ? ` ${bloqueadas.length} selecionada(s) serão ignoradas (já emitida/cancelada/em processamento).`
        : '';

    confirmDialog({
      message: `Confirma o envio de ${emitiveis.length} NFS-e para o Ambiente Nacional?${avisoBloq}\n\nNotas: ${nums}${extra}`,
      header: 'Confirmar emissão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, emitir',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        const ids = emitiveis.map((r) => r.id);
        try {
          await api.post('/nfse/emitidas/lote/emitir', { ids });
          toast.current?.show({
            severity: 'success',
            summary: 'Lote',
            detail: `${ids.length} nota(s) enviada(s) para emissão.`,
          });
          setSelection([]);
          carregar();
        } catch (e) {
          toast.current?.show({
            severity: 'error',
            summary: 'Erro',
            detail: errMsg(e, 'Falha ao emitir lote.'),
          });
        }
      },
    });
  };

  const replicar = async (row) => {
    try {
      const { data } = await api.post(`/nfse/emitidas/${row.id}/replicar`);
      const created = normalize(data);
      toast.current?.show({ severity: 'success', summary: 'Replicada', detail: `Rascunho #${created.id} criado.` });
      carregar();
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: errMsg(e, 'Falha ao replicar.') });
    }
  };

  const cancelar = (row) => {
    confirmDialog({
      message: `Cancelar a NFS-e ${row.numeroNfse || `#${row.id}`}?`,
      header: 'Cancelar NFS-e',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: async () => {
        try {
          await api.post(`/nfse/emitidas/${row.id}/cancelar`, {
            codigoCancelamento: '1',
            motivo: 'Cancelamento solicitado pelo usuário',
          });
          toast.current?.show({ severity: 'success', summary: 'Cancelada', detail: 'NFS-e cancelada.' });
          carregar();
        } catch (e) {
          toast.current?.show({ severity: 'error', summary: 'Erro', detail: errMsg(e, 'Falha ao cancelar.') });
        }
      },
    });
  };

  /** ZIP da seleção — exporta apenas as NFS-e marcadas na grid (por id), em qualquer página. */
  const exportarSelecao = async (formato = 'zip') => {
    const ids = (selection || []).map((r) => r.id);
    if (!ids.length) {
      toast.current?.show({ severity: 'warn', summary: 'Seleção', detail: 'Selecione ao menos uma NFS-e.' });
      return;
    }
    setExportando(true);
    setExportMsg('Gerando ZIP da seleção (XML + PDF). Isso pode levar alguns minutos…');
    try {
      await downloadFromApi(
        API_BASE_URL,
        'nfse/emitidas/exportar',
        { formato, ids: ids.join(',') },
        `nfse-emitidas.${formato === 'zip' ? 'zip' : 'csv'}`
      );
      toast.current?.show({ severity: 'success', summary: 'Export', detail: `Arquivo ${formato.toUpperCase()} gerado (seleção).` });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Export', detail: errMsg(e, e.message || 'Falha ao exportar.') });
    } finally {
      setExportando(false);
      setExportMsg('');
    }
  };

  /**
   * ZIP por filtros — exporta TODAS as NFS-e que casam com os filtros aplicados
   * atualmente (não só as da página atual), sem depender da seleção da grid.
   */
  const exportarPorFiltros = async (formato = 'zip') => {
    if (!filtrosTemDatas(aplicados)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Filtros',
        detail: 'Defina data início e data fim nos filtros para exportar por filtros.',
      });
      return;
    }
    setExportando(true);
    setExportMsg('Gerando ZIP por filtros (XML + PDF). Notas sem PDF em cache serão geradas agora — aguarde…');
    try {
      const params = buildExportFilterParams(aplicados, { agenteIdKey: 'agenteId' });
      await downloadFromApi(API_BASE_URL, 'nfse/emitidas/exportar', params, 'nfse-emitidas.zip');
      toast.current?.show({ severity: 'success', summary: 'Export', detail: 'ZIP por filtros gerado.' });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Export', detail: errMsg(e, e.message || 'Falha ao exportar.') });
    } finally {
      setExportando(false);
      setExportMsg('');
    }
  };

  const baixarModelo = () => {
    try {
      downloadModeloNfseExcel();
      toast.current?.show({ severity: 'success', summary: 'Modelo', detail: 'Arquivo Excel baixado.' });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Modelo', detail: 'Falha ao gerar modelo Excel.' });
    }
  };

  const verXml = async (row) => {
    try {
      const res = await api.get(`/nfse/emitidas/${row.id}/xml`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'XML', detail: errMsg(e, 'XML não disponível.') });
    }
  };

  const verDanfse = async (row) => {
    try {
      const { data } = await api.get(`/nfse/emitidas/${row.id}/pdf`, { timeout: 120000 });
      const content = data?.content ?? data?.Content;
      if (!content) throw new Error('PDF vazio');
      openPdfFromContent(content, `danfse-${row.numeroNfse || row.id}.pdf`);
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'DANFSe',
        detail: errMsg(e, 'Não foi possível gerar o DANFSe (verifique XML/ACBr).'),
        life: 12000,
      });
    }
  };

  const abrirEmail = (row, lote = false) => {
    setEmailTarget(row || null);
    setEmailLote(!!lote);
    setEmailDest('');
    setEmailVisible(true);
  };

  const verDetalhe = async (row) => {
    try {
      const { data } = await api.get(`/nfse/emitidas/${row.id}`);
      setDetalhe(normalize(data));
      setDetalheVisible(true);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Detalhe', detail: errMsg(e, 'Falha ao carregar detalhe.') });
    }
  };

  const enviarEmail = async () => {
    const destinatarios = emailDest
      .split(/[;,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!destinatarios.length) {
      toast.current?.show({ severity: 'warn', summary: 'E-mail', detail: 'Informe ao menos um destinatário.' });
      return;
    }

    const ids = emailLote
      ? (selection || []).map((r) => r.id)
      : emailTarget?.id
        ? [emailTarget.id]
        : [];
    if (!ids.length) {
      toast.current?.show({ severity: 'warn', summary: 'E-mail', detail: 'Nenhuma NFS-e selecionada.' });
      return;
    }

    setEnviandoEmail(true);
    let ok = 0;
    let falhas = 0;
    try {
      for (const id of ids) {
        try {
          await api.post(`/nfse/emitidas/${id}/email`, {
            destinatarios,
            anexarPdf: true,
            anexarXml: true,
          });
          ok += 1;
        } catch {
          falhas += 1;
        }
      }
      toast.current?.show({
        severity: falhas ? 'warn' : 'success',
        summary: 'E-mail',
        detail: falhas
          ? `${ok} enviada(s), ${falhas} com falha.`
          : emailLote
            ? `${ok} e-mail(s) solicitados.`
            : 'Envio solicitado.',
      });
      setEmailVisible(false);
      setEmailLote(false);
    } finally {
      setEnviandoEmail(false);
    }
  };

  const onEscolherExcel = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const linhas = json.map((r) => ({
        tomadorCnpjCpf: String(r.TomadorCnpjCpf || r.tomadorCnpjCpf || r.CnpjCpf || '').trim(),
        tomadorNome: String(r.TomadorNome || r.tomadorNome || r.RazaoSocial || '').trim(),
        valorServico: Number(r.ValorServico || r.valorServico || 0),
        discriminacao: String(r.Discriminacao || r.discriminacao || '').trim(),
        codigoItemServico: String(r.CodigoItemServico || r.codigoItemServico || '').trim(),
        competencia: r.Competencia || r.competencia || null,
        aliquotaIss: Number(r.AliquotaIss || r.aliquotaIss || 0),
      }));
      setImportLinhas(linhas);
      setImportPreview(null);
      setImportStep(2);
      setImportVisible(true);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Importação', detail: 'Não foi possível ler o arquivo.' });
    }
  };

  const abrirImportacao = () => {
    setImportLinhas([]);
    setImportPreview(null);
    setImportStep(1);
    setImportVisible(true);
  };

  const validarImport = async () => {
    setImportando(true);
    setImportStep(3);
    try {
      const { data } = await api.post('/nfse/emitidas/importar', {
        somenteValidar: true,
        linhas: importLinhas,
      });
      const r = normalize(data);
      setImportPreview(r);
      setImportStep(4);
      const qtdErros = r?.erros?.length || 0;
      toast.current?.show({
        severity: qtdErros ? 'warn' : 'success',
        summary: 'Validação',
        detail: qtdErros
          ? `Pré-validação concluída com ${qtdErros} erro(s). Corrija a planilha e importe novamente.`
          : 'Pré-validação concluída sem erros — pode confirmar a importação.',
      });
    } catch (e) {
      setImportStep(2);
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: errMsg(e, 'Falha na validação.') });
    } finally {
      setImportando(false);
    }
  };

  const validacaoOk =
    !!importPreview && importLinhas.length > 0 && (importPreview.erros?.length || 0) === 0;

  const confirmarImport = (emitirApos) => {
    const qtd = importLinhas?.length || 0;
    if (!qtd) {
      toast.current?.show({ severity: 'warn', summary: 'Importação', detail: 'Nenhuma linha para importar.' });
      return;
    }
    if (!validacaoOk) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Importação',
        detail: 'Valide o arquivo e corrija os erros antes de confirmar a importação.',
      });
      return;
    }

    const executar = async () => {
      setImportando(true);
      try {
        const { data } = await api.post('/nfse/emitidas/importar', {
          somenteValidar: false,
          emitirAposImportar: !!emitirApos,
          linhas: importLinhas,
        });
        const r = normalize(data);
        toast.current?.show({
          severity: 'success',
          summary: 'Importação',
          detail: r.message || `${qtd} linha(s) processada(s).`,
        });
        setImportVisible(false);
        setImportLinhas([]);
        setImportPreview(null);
        setImportStep(1);
        carregar();
      } catch (e) {
        toast.current?.show({ severity: 'error', summary: 'Erro', detail: errMsg(e, 'Falha ao importar.') });
      } finally {
        setImportando(false);
      }
    };

    if (!emitirApos) {
      confirmDialog({
        message: `Importar ${qtd} linha(s) como rascunho(s)?`,
        header: 'Confirmar importação',
        icon: 'pi pi-info-circle',
        acceptLabel: 'Sim, importar',
        rejectLabel: 'Cancelar',
        accept: () => {
          executar();
        },
      });
      return;
    }

    confirmDialog({
      message: `Importar ${qtd} linha(s) e enviar imediatamente ao Ambiente Nacional para emissão? Esta ação gera documento fiscal (conforme Homologação/Produção).`,
      header: 'Confirmar importar e emitir',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, importar e emitir',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => {
        executar();
      },
    });
  };

  const columns = [
    { field: 'numeroNfse', header: 'Nº NFS-e' },
    { field: 'serieDps', header: 'Série', style: { width: 80 } },
    { field: 'tomadorNome', header: 'Tomador' },
    { field: 'filialNome', header: 'Empresa / Filial', defaultVisible: true },
    { field: 'competencia', header: 'Competência', style: { width: 110 } },
    {
      field: 'valorServicos',
      header: 'Valor',
      body: (r) => money(r.valorServicos),
      exportValue: (r) => money(r.valorServicos),
      style: { width: 120 },
    },
    {
      field: 'status',
      header: 'Status',
      body: (r) => (
        <Tag className="nfse-status-tag" severity={statusSeverity(r.status)} value={r.status} />
      ),
      exportValue: (r) => r.status,
      style: { width: 140 },
    },
    { field: 'dataEmissao', header: 'Emissão', defaultVisible: false },
    {
      field: '_acoes',
      header: 'Ações',
      sortable: false,
      alwaysVisible: true,
      style: { width: 200 },
      body: (r) => (
        <div className="nfse-row-actions" style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            icon="pi pi-eye"
            rounded
            text
            tooltip="Detalhe"
            onClick={() => verDetalhe(r)}
          />
          <Button
            icon="pi pi-pencil"
            rounded
            text
            tooltip="Editar e reemitir"
            disabled={!podeEditarStatus(r.status)}
            onClick={() => editarNota(r)}
          />
          <Button
            icon="pi pi-file-pdf"
            rounded
            text
            tooltip="DANFSe"
            disabled={r.status !== 'Autorizada'}
            onClick={() => verDanfse(r)}
          />
          <Button
            icon="pi pi-code"
            rounded
            text
            tooltip="XML"
            disabled={r.status !== 'Autorizada'}
            onClick={() => verXml(r)}
          />
          <Button
            icon="pi pi-copy"
            rounded
            text
            tooltip="Replicar"
            onClick={() => replicar(r)}
          />
        </div>
      ),
    },
  ];

  const salvarFiltrosCfg = () => {
    setFiltrosVisiveis(draftVisiveis);
    try {
      localStorage.setItem('nfse-filtros-visiveis:emissor', JSON.stringify(draftVisiveis));
    } catch {
      // ignore
    }
    setFiltrosCfgVisible(false);
  };

  return (
    <div className="nfse-consulta-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <Dialog
        header="Exportando XML + PDF"
        visible={exportando}
        closable={false}
        modal
        style={{ width: 420 }}
        onHide={() => {}}
      >
        <p style={{ marginTop: 0 }}>{exportMsg || 'Processando…'}</p>
        <ProgressBar mode="indeterminate" style={{ height: 10 }} />
        <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 0 }}>
          Não feche esta página até o download iniciar.
        </p>
      </Dialog>

      <div className="nfse-consulta-header">
        <h1>NFSe Emissor</h1>
        <div className="nfse-consulta-header-actions">
          <Button
            label="ZIP por filtros (XML+PDF)"
            icon="pi pi-filter"
            className="p-button-outlined"
            disabled={!filtrosTemDatas(aplicados) || exportando}
            loading={exportando}
            onClick={() => exportarPorFiltros('zip')}
            tooltip="Exporta XML+PDF de TODAS as NFS-e que casam com os filtros aplicados (todas as páginas), não só a seleção atual. Exige período (data início/fim) definido."
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            label="Importar Excel"
            icon="pi pi-upload"
            className="p-button-outlined"
            onClick={abrirImportacao}
          />
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={onEscolherExcel}
          />
          <Button
            label="Nota de ajuste"
            icon="pi pi-sliders-h"
            className="p-button-outlined"
            onClick={() => navigate('/nfse/ajuste')}
          />
          <Button
            label="Nova NFSe"
            icon="pi pi-plus"
            onClick={() => navigate('/nfse/emissor/nova')}
          />
        </div>
        <p className="nfse-consulta-header-hint">
          <strong>ZIP por filtros</strong> exporta todas as notas do período/filtros atuais
          (todas as páginas). <strong>ZIP da seleção</strong> (na barra abaixo da grid) exporta só
          as notas marcadas na tabela.
        </p>
      </div>

      <div className="nfse-filtros-card">
        <div className="nfse-filtros-card-head">
          <h2>Filtros</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              label="Configurar filtros"
              icon="pi pi-sliders-h"
              className="p-button-text p-button-sm nfse-filtros-toggle"
              onClick={() => {
                setDraftVisiveis(filtrosVisiveis);
                setFiltrosCfgVisible(true);
              }}
            />
            <Button
              label={filtrosAbertos ? 'Ocultar' : 'Mostrar'}
              icon={filtrosAbertos ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
              className="p-button-text p-button-sm nfse-filtros-toggle"
              onClick={() => setFiltrosAbertos((v) => !v)}
            />
          </div>
        </div>

        {filtrosAbertos && (
          <div className="nfse-filtros-grid">
            <div className="nfse-filtro-field">
              <label>Data início</label>
              <Calendar
                value={filtros.dataInicio}
                onChange={(e) => setCampo('dataInicio', e.value)}
                dateFormat="dd/mm/yy"
                locale="pt"
                showIcon
                icon="pi pi-calendar"
                placeholder="Início"
              />
            </div>
            <div className="nfse-filtro-field">
              <label>Data fim</label>
              <Calendar
                value={filtros.dataFim}
                onChange={(e) => setCampo('dataFim', e.value)}
                dateFormat="dd/mm/yy"
                locale="pt"
                showIcon
                icon="pi pi-calendar"
                placeholder="Fim"
              />
            </div>
            <div className="nfse-filtro-field nfse-filtro-field-xl">
              <label>Empresa / Filial</label>
              <Dropdown
                value={filtros.filialId}
                options={filialOptions}
                onChange={(e) => setCampo('filialId', e.value)}
                placeholder="Todas"
                filter
                filterBy="search,label,razao,fantasia,cnpjcpf"
                itemTemplate={richItemTemplate}
                valueTemplate={richValueTemplate}
                panelClassName="rich-select-panel"
                optionLabel="label"
              />
            </div>
            <div className="nfse-filtro-field nfse-filtro-field-xl">
              <label>Tomador (cliente)</label>
              <Dropdown
                value={filtros.agenteId}
                options={agenteOptions}
                onChange={(e) => setCampo('agenteId', e.value)}
                placeholder="Todos"
                filter
                filterBy="search,label,razao,fantasia,cnpjcpf"
                itemTemplate={richItemTemplate}
                valueTemplate={richValueTemplate}
                panelClassName="rich-select-panel"
                optionLabel="label"
                showClear
                emptyMessage="Nenhum cliente nesta filial"
              />
            </div>
            <div className="nfse-filtro-field">
              <label>Status</label>
              <Dropdown
                value={filtros.status}
                options={STATUS_EMISSOR}
                onChange={(e) => setCampo('status', e.value)}
              />
            </div>
            {filtrosVisiveis.includes('numero') && (
              <div className="nfse-filtro-field">
                <label>Número NFS-e</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.numero}
                  onChange={(e) => setCampo('numero', e.target.value)}
                />
              </div>
            )}
            {filtrosVisiveis.includes('chave') && (
              <div className="nfse-filtro-field nfse-filtro-field-wide">
                <label>Chave de acesso</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.chave}
                  onChange={(e) => setCampo('chave', e.target.value)}
                />
              </div>
            )}
            {filtrosVisiveis.includes('serie') && (
              <div className="nfse-filtro-field">
                <label>Série DPS</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.serie}
                  onChange={(e) => setCampo('serie', e.target.value)}
                />
              </div>
            )}
            <div className="nfse-filtros-actions">
              <Button label="Limpar" className="p-button-text" onClick={limpar} />
              <Button label="Aplicar" icon="pi pi-filter" onClick={aplicar} />
            </div>
          </div>
        )}
      </div>

      {selection?.length > 0 && (
        <div className="nfse-lote-bar">
          <strong>{selection.length}</strong> selecionada(s)
          <Button
            label={`Emitir seleção${
              (selection || []).filter((r) => podeEmitirStatus(r.status)).length
                ? ` (${(selection || []).filter((r) => podeEmitirStatus(r.status)).length})`
                : ''
            }`}
            icon="pi pi-send"
            className="p-button-sm"
            onClick={emitirSelecao}
            disabled={!(selection || []).some((r) => podeEmitirStatus(r.status))}
            tooltip={
              (selection || []).some((r) => podeEmitirStatus(r.status))
                ? 'Envia ao Ambiente Nacional (pede confirmação)'
                : 'Só Rascunho / Rejeitada / Erro de comunicação podem ser emitidos'
            }
          />
          <Button
            label="ZIP da seleção"
            icon="pi pi-file"
            className="p-button-sm p-button-outlined"
            loading={exportando}
            disabled={exportando}
            onClick={() => exportarSelecao('zip')}
            tooltip="Exporta XML+PDF apenas das NFS-e marcadas nesta grid"
          />
          <Button
            label="E-mail da seleção"
            icon="pi pi-envelope"
            className="p-button-sm p-button-outlined"
            onClick={() => abrirEmail(null, true)}
            tooltip="Envia e-mail com XML/PDF das NFS-e selecionadas"
          />
        </div>
      )}

      <CadastroDataTable
        value={rows}
        columns={columns}
        storageKey="nfse-emissor"
        loading={loading}
        title="NFSe Emissor"
        emptyMessage="Nenhuma NFS-e encontrada com os filtros atuais."
        globalFilterFields={['numeroNfse', 'tomadorNome', 'filialNome', 'status', 'competencia']}
        selectionMode="checkbox"
        selection={selection}
        onSelectionChange={(e) => setSelection(e.value || [])}
        rows={50}
        rowsPerPageOptions={[25, 50, 100, 250]}
      />

      <Dialog
        header="Configurar filtros"
        visible={filtrosCfgVisible}
        onHide={() => setFiltrosCfgVisible(false)}
        style={{ width: 420 }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancelar" className="p-button-text" onClick={() => setFiltrosCfgVisible(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={salvarFiltrosCfg} />
          </div>
        }
      >
        <p style={{ color: '#64748b', marginTop: 0 }}>
          Período, Empresa/Filial, Tomador e Status são fixos. Marque os filtros adicionais:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FILTROS_OPCIONAIS.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Checkbox
                inputId={`flt-${f.id}`}
                checked={draftVisiveis.includes(f.id)}
                onChange={(e) => {
                  setDraftVisiveis((prev) =>
                    e.checked ? [...prev, f.id] : prev.filter((x) => x !== f.id)
                  );
                }}
              />
              <label htmlFor={`flt-${f.id}`} style={{ cursor: 'pointer', margin: 0 }}>
                {f.label}
              </label>
            </div>
          ))}
        </div>
      </Dialog>

      <Dialog
        header="Importar NFS-e via Excel"
        visible={importVisible}
        onHide={() => setImportVisible(false)}
        style={{ width: 640 }}
        breakpoints={{ '720px': '95vw' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <Button label="Fechar" className="p-button-text" onClick={() => setImportVisible(false)} />
            {importLinhas.length > 0 && (
              <Button
                label="Validar"
                icon="pi pi-check"
                className="p-button-outlined"
                loading={importando}
                onClick={validarImport}
              />
            )}
            <Button
              label="Importar rascunhos"
              icon="pi pi-upload"
              loading={importando}
              disabled={!validacaoOk}
              onClick={() => confirmarImport(false)}
            />
            <Button
              label="Importar e emitir"
              icon="pi pi-send"
              loading={importando}
              disabled={!validacaoOk}
              onClick={() => confirmarImport(true)}
            />
          </div>
        }
      >
        <div className="nfse-import-wizard">
          <ol className="nfse-import-wizard-steps">
            <li className={importStep >= 1 ? 'done' : ''}>Baixe o modelo (opcional, se ainda não tiver a planilha)</li>
            <li className={importStep >= 2 ? 'done' : ''}>Preencha e selecione o arquivo Excel</li>
            <li className={importStep >= 3 ? 'done' : ''}>Valide o conteúdo antes de importar</li>
            <li className={importStep >= 4 && !validacaoOk ? 'has-error' : importStep >= 4 ? 'done' : ''}>
              Corrija erros indicados (se houver)
            </li>
            <li className={validacaoOk ? 'done' : ''}>Confirme a importação (rascunho ou já emitindo)</li>
          </ol>

          <div className="nfse-import-wizard-actions">
            <Button
              label="Baixar modelo (Excel)"
              icon="pi pi-download"
              className="p-button-outlined p-button-sm"
              onClick={baixarModelo}
            />
            <Button
              label={importLinhas.length ? 'Selecionar outro arquivo' : 'Selecionar arquivo Excel'}
              icon="pi pi-upload"
              className="p-button-sm"
              onClick={() => fileRef.current?.click()}
            />
          </div>

          <p className="nfse-import-wizard-count">
            {importLinhas.length
              ? `${importLinhas.length} linha(s) lida(s) do Excel.`
              : 'Nenhum arquivo selecionado ainda.'}
          </p>

          {importando && importStep === 3 && (
            <ProgressBar mode="indeterminate" style={{ height: 8, marginBottom: 12 }} />
          )}

          {importPreview && (
            <div className={`nfse-import-wizard-result ${validacaoOk ? 'ok' : 'error'}`}>
              {validacaoOk ? (
                <p>
                  <i className="pi pi-check-circle" /> Validação concluída sem erros — {importPreview.ok || 0} linha(s)
                  pronta(s) para importar.
                </p>
              ) : (
                <>
                  <p>
                    <i className="pi pi-exclamation-triangle" /> {importPreview.erros?.length || 0} erro(s)
                    encontrado(s). Corrija a planilha e selecione o arquivo novamente.
                  </p>
                  <ul className="nfse-import-wizard-erros">
                    {(importPreview.erros || []).slice(0, 30).map((e, idx) => (
                      <li key={`${e.linha}-${idx}`}>
                        Linha {e.linha}: {e.erro || e.mensagem}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        header={`Enviar e-mail — ${
          emailLote
            ? `${selection?.length || 0} NFS-e selecionada(s)`
            : `NFS-e ${emailTarget?.numeroNfse || emailTarget?.id || ''}`
        }`}
        visible={emailVisible}
        onHide={() => {
          setEmailVisible(false);
          setEmailLote(false);
        }}
        style={{ width: 440 }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              label="Cancelar"
              className="p-button-text"
              onClick={() => {
                setEmailVisible(false);
                setEmailLote(false);
              }}
            />
            <Button label="Enviar" icon="pi pi-send" loading={enviandoEmail} onClick={enviarEmail} />
          </div>
        }
      >
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Destinatários</label>
        <InputText
          value={emailDest}
          onChange={(e) => setEmailDest(e.target.value)}
          placeholder="email1@empresa.com; email2@empresa.com"
          className="w-full"
          style={{ width: '100%' }}
        />
      </Dialog>

      <Dialog
        header={detalhe ? `NFS-e ${detalhe.numeroNfse || detalhe.id}` : 'Detalhe'}
        visible={detalheVisible}
        onHide={() => setDetalheVisible(false)}
        style={{ width: 'min(720px, 96vw)' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <Button
              label="Editar"
              icon="pi pi-pencil"
              className="p-button-outlined"
              disabled={!detalhe || !podeEditarStatus(detalhe.status)}
              onClick={() => {
                setDetalheVisible(false);
                editarNota(detalhe);
              }}
            />
            <Button
              label="E-mail"
              icon="pi pi-envelope"
              className="p-button-outlined"
              onClick={() => {
                setDetalheVisible(false);
                abrirEmail(detalhe, false);
              }}
            />
            <Button
              label="Nota de ajuste"
              icon="pi pi-sliders-h"
              className="p-button-outlined"
              disabled={detalhe?.status !== 'Autorizada'}
              onClick={() => navigate(`/nfse/ajuste?refId=${detalhe?.id}`)}
            />
            <Button
              label="Cancelar NFS-e"
              icon="pi pi-times"
              className="p-button-outlined p-button-danger"
              disabled={!detalhe || detalhe.status === 'Cancelada' || detalhe.status === 'Rascunho'}
              onClick={() => {
                setDetalheVisible(false);
                cancelar(detalhe);
              }}
            />
            <Button label="Fechar" onClick={() => setDetalheVisible(false)} />
          </div>
        }
      >
        {detalhe && (
          <div className="nfse-detalhe-grid">
            <div><strong>Tomador</strong><br />{detalhe.tomadorNome || '—'}</div>
            <div><strong>Empresa / Filial</strong><br />{detalhe.filialNome || '—'}</div>
            <div><strong>Status</strong><br />{detalhe.status}</div>
            <div><strong>Competência</strong><br />{detalhe.competencia}</div>
            <div><strong>Valor serviços</strong><br />{money(detalhe.valorServicos)}</div>
            <div><strong>Item serviço</strong><br />{detalhe.codigoItemServico || '—'}</div>
            <div><strong>Série / DPS</strong><br />{detalhe.serieDps || '—'} / {detalhe.numeroDps || '—'}</div>
            <div><strong>Ambiente</strong><br />{detalhe.ambiente || '—'}</div>
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>Chave</strong>
              <br />
              <code style={{ wordBreak: 'break-all' }}>{detalhe.chaveAcesso || '—'}</code>
            </div>
            {(detalhe.mensagemErro || detalhe.mensagemAmigavel) && (
              <div style={{ gridColumn: '1 / -1', background: '#fef2f2', padding: 10, borderRadius: 6 }}>
                <strong>Erro</strong>
                <br />
                {detalhe.mensagemAmigavel || detalhe.mensagemErro}
                {detalhe.comoResolver ? (
                  <>
                    <br />
                    <span style={{ color: '#64748b' }}>Como resolver: {detalhe.comoResolver}</span>
                  </>
                ) : null}
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>Discriminação</strong>
              <br />
              {detalhe.discriminacao || '—'}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
